import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { animate, style, transition, trigger } from '@angular/animations';
import {
  AppMetaDataItemNames,
  CourseLessonEntity,
  QuizEntity,
  QuizQuestionLinkEntity,
  QuizResultEntity,
  QuizResultQuestionEntity,
  QuizResultStatusAllItems
} from '../../../../meta-data/app-meta-data.service';
import { QuizCompleteResult } from '../../models/quiz-complete-result.model';
import { DateTimeUtilService } from '../../../shared/util/date-time-util.service';
import { ArrayUtilService } from '../../../shared/util/array-util.service';
import { QuizRepositoryService } from '../../services/quiz-repository.service';
import { LessonProgressHierarchy } from '../../../courses/models/lesson-progress-hierarchy.model';
import { QuizUtilService } from '../../services/quiz-util.service';
import { QuizSetupResult } from '../../models/quiz-setup-result.model';
import { DwMessage, DwMessageSeverity } from '@devwareapps/devware-cap';
import { LessonFlagUtilService } from '../../../courses/services/lesson-flag-util.service';
import { InactivityUtilService } from '../../../shared/util/inactivity-util.service';
import { HelpScoutBeaconService } from '../../../help-scout-beacon/services/help-scout-beacon.service';
import { environment } from '../../../../../environments/environment';
import { QuizTestModeOptions } from '../../models/quiz-test-mode-options.model';

/** Discriminated union for chip strip items. Bundling the trailing-anchor
 *  chip and its ellipsis into the same array as the windowed chips means
 *  the highest-index chip is a single DOM node across the transition where
 *  it goes from "trailing anchor" → "last window chip" — Angular's *ngFor
 *  trackBy keeps it stable and only the ellipsis enters/leaves. */
type StepItem =
  | { kind: 'chip'; index: number; stateClass: string; isFlagged: boolean }
  | { kind: 'ellipsis' };

@Component({
  selector: 'app-quiz',
  templateUrl: './quiz.component.html',
  styleUrls: ['./quiz.component.scss'],
  animations: [
    // Pill enter/leave for the windowed chip strip. Scale-and-fade so the
    // chips that slide in/out as the window moves don't pop discretely.
    // ease-out on enter, ease-in on leave per Emil's motion guidance. The
    // tokens used here (--easing-out etc.) live in cap-variables.scss but
    // Angular animations don't read CSS vars, so we hardcode the cubic-
    // bezier values to match — kept in sync with the design system.
    trigger('chipMotion', [
      transition(':enter', [
        // Collapse width + padding to zero on enter and expand to natural
        // size — this is the key trick: without it, the entering chip
        // claims its full layout footprint instantly, and during the
        // brief :enter/:leave overlap the strip momentarily holds one
        // extra item which overflows the card and wraps the next-chevron
        // to a second row. The '*' placeholders mean "whatever the
        // computed style is at rest" so mobile (52px) and desktop (64px)
        // chip widths are both handled automatically.
        style({ opacity: 0, width: 0, marginLeft: 0, marginRight: 0 }),
        animate('220ms cubic-bezier(0.2, 0, 0, 1)',
          style({ opacity: 1, width: '*', marginLeft: '*', marginRight: '*' }))
      ]),
      transition(':leave', [
        animate('140ms cubic-bezier(0.4, 0, 1, 1)',
          style({ opacity: 0, width: 0, marginLeft: 0, marginRight: 0 }))
      ])
    ]),
    // Ellipsis collapses the same way so it doesn't reserve a phantom
    // gap when it leaves (or hold one open before its full opacity).
    trigger('ellipsisMotion', [
      transition(':enter', [
        style({ opacity: 0, width: 0, marginLeft: 0, marginRight: 0 }),
        animate('160ms ease-out',
          style({ opacity: 1, width: '*', marginLeft: '*', marginRight: '*' }))
      ]),
      transition(':leave', [
        animate('100ms ease-in',
          style({ opacity: 0, width: 0, marginLeft: 0, marginRight: 0 }))
      ])
    ])
  ]
})
export class QuizComponent implements OnInit, OnDestroy, OnChanges {
  private stepBarElement?: ElementRef<HTMLElement>;

  @ViewChild('stepBar', { static: false })
  private set stepBar(element: ElementRef<HTMLElement> | undefined) {
    this.stepBarElement = element;
    if (element) {
      this.bindStepBarResizeObserver();
    } else {
      this.teardownStepBarResizeObserver();
    }
  }

  @Input() quiz: QuizEntity;
  @Input() quizResult: QuizResultEntity;
  @Input() providedQuizSetup: QuizSetupResult;
  @Input() progress: LessonProgressHierarchy;
  @Input() lesson: CourseLessonEntity;

  @Output() quizComplete = new EventEmitter<QuizCompleteResult>();
  @Output() quizSubmissionStarted = new EventEmitter<void>();
  @Output() quizSubmissionFailed = new EventEmitter<void>();

  @Input() reviewAnswers: boolean = false;

  @Input() startingQuizResultQuestionId: number;

  @Input() testModeOptions: QuizTestModeOptions;

  testMode: boolean = false;

  currentQuestionLink: QuizQuestionLinkEntity;
  currentQuestionResult: QuizResultQuestionEntity;

  hasNextQuestion: boolean = false;
  hasPreviousQuestion: boolean = false;
  //questionLinkIds: number[];

  quizStartTime: any;

  timeString: string;
  currentQuestionIndex?: number;
  totalQuestionCount?: number;
  quizTimerIntervalHandle: any;
  currentQuestionStartTime: any;
  timeTakingString: string;
  timeLabel: string = 'Time :';
  quizSetup: QuizSetupResult;

  isLoading = true;
  showQuiz = false;

  questionResultIconClass: string;
  questionResultTextNote: string;

  /** QuizQuestionLinkIds the user has flagged for review in this attempt.
   *  Per-question so flagging Q1 doesn't disable the button on Q2/Q3. State
   *  is in-memory only — submitting a flag opens a modal that persists to
   *  the backend (lessonFlagUtilService), but the visual "flagged" toggle
   *  resets on a fresh attempt. */
  flaggedQuestionLinkIds = new Set<number>();

  /* =============================================================================
     Header card derived data — feeds the redesigned `.quiz-shell-header` panel
     above the question card. All four helpers are getters so they re-evaluate
     during change detection as `currentQuestionIndex` advances and the user
     answers questions (which flips chips from is-future → is-done).
     ========================================================================== */

  /** "Module 3 · Knowledge Check" — uppercased in CSS via tracking-caps. */
  get moduleEyebrowLabel(): string {
    const order = this.progress?.module?.module?.ModuleOrder;
    const type = this.quiz?.QuizTypeIdDisplay || 'Quiz';
    return order != null ? `Module ${order} · ${type}` : type;
  }

  /** Header card h1 — defaults to the quiz name if no module context. */
  get moduleHeadingLabel(): string {
    return this.progress?.module?.module?.ModuleName || this.quiz?.QuizName || '';
  }

  /** Question-card eyebrow — mode-aware copy paired with the dot color. */
  get questionEyebrowLabel(): string {
    const idx = (this.currentQuestionIndex ?? 0) + 1;
    const total = this.totalQuestionCount ?? 0;
    if (this.testMode) return `Test mode · Step ${idx} of ${total}`;
    if (this.reviewAnswers) return `Reviewing answers · Step ${idx} of ${total}`;
    return `Now answering · Step ${idx} of ${total}`;
  }

  /** Modifier class on the question card so SCSS can flip eyebrow color. */
  get modeStateClass(): string {
    if (this.testMode) return 'is-test-mode';
    if (this.reviewAnswers) return 'is-reviewing';
    return 'is-attempting';
  }

  /** 1-based step counter for the compact "Step N of Y" indicator shown at
   *  narrow widths where the chip strip can't physically fit. */
  get stepNumber(): number {
    return (this.currentQuestionIndex ?? 0) + 1;
  }

  /** Percentage progress through the attempt — drives the compact track fill. */
  stepPercent = 0;

  /** True if the current question has already been flagged in this attempt. */
  isCurrentQuestionFlagged = false;

  /** Step-bar chips — one per question in the current attempt order. Kept
   *  for the rare consumer that needs the full list; the header chip strip
   *  now renders {@link visibleSteps} instead so 60-question final exams
   *  don't pour out a 60-circle row. */
  steps: { stateClass: string }[] = [];

  /** Chip strip items — the centered window of chips, plus a trailing
   *  ellipsis + last-question anchor when the window doesn't already cover
   *  the end. Combining everything into a single discriminated-union array
   *  is the key trick for smooth window-slide animations: the chip at the
   *  highest visible index is the same DOM node whether it's the trailing
   *  anchor or the rightmost window chip, so it doesn't enter/leave when
   *  the user crosses the boundary — only the ellipsis does. */
  visibleSteps: StepItem[] = [];

  private buildVisibleSteps(): StepItem[] {
    if (!this.quizSetup || !this.totalQuestionCount) {
      return [];
    }

    const total = this.totalQuestionCount;
    const size = Math.min(this.stepWindowSize, total);
    const half = Math.floor(size / 2);
    const current = this.currentQuestionIndex ?? 0;

    // Clamp the window so we never produce a partial window at the
    // boundaries — at q1 the window is [1..7], at qN it's [N-6..N].
    let start = current - half;
    start = Math.max(0, Math.min(start, total - size));

    const items: StepItem[] = Array.from({ length: size }, (_, i) => {
      const index = start + i;
      return this.buildChipItem(index);
    });

    // Trailing anchor — last question chip, prefixed with an ellipsis.
    // Skipped when the last index is already inside the window (otherwise
    // we'd render the same chip twice). The ellipsis sits between the
    // window's last chip and the anchor; when the window expands to
    // include the anchor's index, the ellipsis exits via the leave
    // animation and the anchor chip is no longer a separate DOM node
    // (its index is now part of the window).
    const lastIdx = total - 1;
    if (start + size <= lastIdx) {
      items.push({ kind: 'ellipsis' });
      items.push(this.buildChipItem(lastIdx));
    }

    return items;
  }

  private buildChipItem(index: number): StepItem {
    const qlId = this.quizSetup.CurrentQuizQuestionLinkIds[index];
    return {
      kind: 'chip',
      index,
      stateClass: this.getStepStateClass(index, qlId),
      isFlagged: this.flaggedQuestionLinkIds.has(qlId)
    };
  }

  /** Window width in chips. This is based on the rendered step-bar width,
   *  not the viewport, because the quiz card's own padding/gutters can make
   *  an iPhone-sized viewport much tighter than `window.innerWidth` implies. */
  private stepWindowSizeValue = 3;
  private get stepWindowSize(): number {
    return this.stepWindowSizeValue;
  }

  /** True when every chip fits without windowing — used to switch the step
   *  bar into a compact layout where the chevrons hug the chip cluster
   *  instead of pinning to the card edges. */
  get isStepBarCompact(): boolean {
    return !!this.totalQuestionCount && this.totalQuestionCount <= this.stepWindowSize;
  }

  private stepBarResizeObserver?: { disconnect(): void };
  private stepBarResizeListener = (): void => {
    this.ngZone.run(() => this.updateStepWindowSizeFromElement());
  };

  /** Tracks the OS-level `prefers-reduced-motion: reduce` preference and
   *  short-circuits the entire Angular animation system on this component
   *  via `@.disabled` when set. Covers chipMotion + ellipsisMotion (the
   *  two triggers in this file) plus any future ones — safer than gating
   *  each trigger by duration. Bound in ngOnInit and torn down in
   *  ngOnDestroy. */
  private reducedMotionMediaQuery: MediaQueryList | null = null;
  private reducedMotionListener = (event: MediaQueryListEvent): void => {
    this.prefersReducedMotion = event.matches;
  };
  prefersReducedMotion = false;

  @HostBinding('@.disabled')
  get animationsDisabled(): boolean {
    return this.prefersReducedMotion;
  }

  private getStepStateClass(index: number, questionLinkId: number): string {
    if (index === this.currentQuestionIndex) {
      return 'is-current';
    }

    const ql = this.quizSetup?.AllQuizQuestionLinks?.find(q => q.QuizQuestionLinkId === questionLinkId);
    if (!ql) {
      return 'is-future';
    }

    const result = this.quizResult?.QuizResultQuestion?.find(r => r.QuestionId === ql.QuestionId);

    if (this.reviewAnswers) {
      // Review mode collapses the done/future distinction into correctness —
      // every question has been answered by definition (you submitted to get
      // here), so an unrecognised result still falls back to is-future as a
      // defensive default rather than asserting correctness either way.
      if (result?.IsCorrect === true) return 'is-correct';
      if (result?.IsCorrect === false) return 'is-incorrect';
      return 'is-future';
    }

    return result?.IsAnswered ? 'is-done' : 'is-future';
  }

  constructor(private dateTimeUtilService: DateTimeUtilService,
    private arrayUtilService: ArrayUtilService,
    private quizRepositoryService: QuizRepositoryService,
    private quizUtilService: QuizUtilService,
    private lessonFlagUtilService: LessonFlagUtilService,
    private inactivityUtilService: InactivityUtilService,
    private helpScoutBeaconService: HelpScoutBeaconService,
    private changeDetectorRef: ChangeDetectorRef,
    private ngZone: NgZone

  ) { }


  ngOnInit(): void {
    this.setupQuiz();
    this.bindReducedMotionMediaQuery();

    if (environment.turnOffBeaconForQuizzes) {
      this.helpScoutBeaconService.turnOffBeacon();
    }
  }

  /** Re-init when the parent swaps in a different quiz on the same
   *  component instance. quiz-home sets `this.quiz = quiz` (rather than
   *  destroying the child) when the user navigates between quiz lessons
   *  in the sidebar, so without this hook the timer + question state
   *  from the previous quiz would keep running while the new quiz's
   *  module / heading text rendered around it. We gate on QuizId change
   *  and skip the first-change pass — that one is owned by ngOnInit. */
  ngOnChanges(changes: SimpleChanges): void {
    const quizChange = changes['quiz'];
    if (!quizChange || quizChange.firstChange) {
      return;
    }
    const prevId = quizChange.previousValue?.QuizId;
    const nextId = quizChange.currentValue?.QuizId;
    if (prevId === nextId) {
      return;
    }
    this.resetQuizState();
    this.setupQuiz();
  }

  ngOnDestroy(): void {
    // clearInterval was previously missing here — the timer's setInterval
    // closure kept a reference to the component long after the view was
    // destroyed, ticking forever and leaking memory. Belt-and-suspenders
    // with resetQuizState() to make sure subscribers, timers, and the
    // help-scout beacon are all returned to their idle state on teardown.
    this.resetQuizState();
    this.helpScoutBeaconService.turnOnBeacon();
    this.teardownStepBarResizeObserver();
    this.reducedMotionMediaQuery?.removeEventListener('change', this.reducedMotionListener);
  }

  /** Tears down any in-flight quiz state so the component can either be
   *  re-initialised against a freshly-supplied @Input quiz (ngOnChanges)
   *  or shut down cleanly (ngOnDestroy). Idempotent. */
  private resetQuizState(): void {
    if (this.quizTimerIntervalHandle) {
      clearInterval(this.quizTimerIntervalHandle);
      this.quizTimerIntervalHandle = null;
    }
    this.quizSetup = undefined as any;
    this.quizResult = undefined as any;
    this.currentQuestionLink = undefined as any;
    this.currentQuestionResult = undefined as any;
    this.currentQuestionIndex = undefined;
    this.totalQuestionCount = undefined;
    this.flaggedQuestionLinkIds.clear();
    this.timeString = '';
    this.timeTakingString = '';
    this.hasNextQuestion = false;
    this.hasPreviousQuestion = false;
    this.testMode = false;
    this.grading = false;
    this.showQuiz = false;
    this.message = undefined;
    this.isLoading = true;
  }

  /** Initial step-bar measurement + listener for live card-width changes.
   *  ResizeObserver keeps the step count tied to the actual card contents
   *  instead of the viewport, which fixes narrow padded mobile cards and
   *  future split/sidebar layouts. Typed loosely because TS 4.0's lib.dom
   *  does not ship ResizeObserver typings in this project. */
  private bindStepBarResizeObserver(): void {
    if (typeof window === 'undefined') {
      return;
    }

    const element = this.stepBarElement?.nativeElement;
    if (!element) {
      return;
    }

    this.teardownStepBarResizeObserver();
    this.scheduleStepWindowMeasure();

    const ResizeObserverCtor = (window as any).ResizeObserver;
    if (ResizeObserverCtor) {
      const observer = new ResizeObserverCtor((entries: any[]) => {
        const width = entries?.[0]?.contentRect?.width || element.clientWidth;
        this.ngZone.run(() => this.updateStepWindowSizeForWidth(width));
      });
      observer.observe(element);
      this.stepBarResizeObserver = observer;
      return;
    }

    window.addEventListener('resize', this.stepBarResizeListener);
  }

  private scheduleStepWindowMeasure(): void {
    const measure = () => this.ngZone.run(() => this.updateStepWindowSizeFromElement());
    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(measure);
      return;
    }

    setTimeout(measure);
  }

  private teardownStepBarResizeObserver(): void {
    this.stepBarResizeObserver?.disconnect();
    this.stepBarResizeObserver = undefined;
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.stepBarResizeListener);
    }
  }

  private updateStepWindowSizeFromElement(): void {
    const element = this.stepBarElement?.nativeElement;
    if (!element) {
      return;
    }

    const width = element.clientWidth
      || (typeof window !== 'undefined' ? window.innerWidth : 0);
    this.updateStepWindowSizeForWidth(width);
  }

  private updateStepWindowSizeForWidth(width: number): void {
    const nextSize = this.getStepWindowSizeForWidth(width);
    if (nextSize === this.stepWindowSizeValue) {
      return;
    }

    this.stepWindowSizeValue = nextSize;
    this.refreshStepViewModel();
    this.changeDetectorRef.markForCheck();
  }

  private refreshStepViewModel(): void {
    if (!this.quizSetup) {
      this.steps = [];
      this.visibleSteps = [];
      this.stepPercent = 0;
      this.isCurrentQuestionFlagged = false;
      return;
    }

    this.steps = this.quizSetup.CurrentQuizQuestionLinkIds.map((questionLinkId, index) => ({
      stateClass: this.getStepStateClass(index, questionLinkId)
    }));
    this.visibleSteps = this.buildVisibleSteps();
    this.stepPercent = this.steps.length ? (this.stepNumber / this.steps.length) * 100 : 0;

    const currentQuestionLinkId = this.currentQuestionLink?.QuizQuestionLinkId;
    this.isCurrentQuestionFlagged = currentQuestionLinkId != null
      && this.flaggedQuestionLinkIds.has(currentQuestionLinkId);
  }

  private getStepWindowSizeForWidth(width: number): number {
    if (width >= 680) {
      return 7;
    }
    if (width >= 560) {
      return 5;
    }
    return 3;
  }

  /** Reduced-motion preference listener. Guarded so legacy SSR / test
   *  environments without matchMedia still boot (defaulting to animations-
   *  enabled — the safer fallback because most users don't have reduce set,
   *  and the chips animations are short and non-disorienting at the default
   *  duration). */
  private bindReducedMotionMediaQuery(): void {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return;
    }
    this.reducedMotionMediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.prefersReducedMotion = this.reducedMotionMediaQuery.matches;
    this.reducedMotionMediaQuery.addEventListener('change', this.reducedMotionListener);
  }

  /** Jump to an arbitrary question by 0-based index. No-ops on the current
   *  index, out-of-range values, or when the quiz isn't fully set up.
   *  Skipping an unanswered question is intentionally allowed — the final
   *  Submit button still requires the last question to be answered, and
   *  unanswered chips are visually distinct in the strip so students can
   *  spot what they left behind. */
  goToQuestion(index: number): void {
    if (!this.quizSetup || this.totalQuestionCount == null) {
      return;
    }
    if (index < 0 || index >= this.totalQuestionCount) {
      return;
    }
    if (index === this.currentQuestionIndex) {
      return;
    }
    this.currentQuestionIndex = index;
    this.setupCurrentQuestion();
  }

  /** ngFor trackBy — chip identity is its question index; the ellipsis is
   *  a singleton so a string key suffices. Stable identity means the same
   *  chip morphs (e.g. is-future → is-current) on navigation instead of
   *  being torn down and recreated, AND the highest-index chip is the
   *  same DOM node whether it's the trailing anchor or part of the window
   *  — so it never enter/leave-animates when the boundary is crossed. */
  trackByStepItem(_pos: number, item: StepItem): string | number {
    return item.kind === 'chip' ? item.index : 'ellipsis';
  }

  /** Aria label for a chip — combines position with state so screen-reader
   *  users get the same context sighted users get from the chip color. */
  chipAriaLabel(step: { index: number; stateClass: string; isFlagged: boolean }): string {
    const parts = [`Question ${step.index + 1}`];
    if (step.index === this.currentQuestionIndex) {
      parts.push('current');
    } else if (step.stateClass === 'is-done') {
      parts.push('answered');
    } else if (step.stateClass === 'is-correct') {
      parts.push('correct');
    } else if (step.stateClass === 'is-incorrect') {
      parts.push('incorrect');
    } else {
      parts.push('unanswered');
    }
    if (step.isFlagged) {
      parts.push('flagged for review');
    }
    return parts.join(', ');
  }

  flagLesson() {
    if (!this.currentQuestionLink || this.isCurrentQuestionFlagged) {
      return;
    }

    const linkId = this.currentQuestionLink.QuizQuestionLinkId;

    this.lessonFlagUtilService.showQuestionFlagModal(this.progress.course.course.CourseId, this.progress.lesson.lesson.CourseLessonId, this.quiz.QuizId, this.currentQuestionLink.QuestionId)
      .subscribe(result => {
        if (!result.cancelled) {
          this.flaggedQuestionLinkIds.add(linkId);
          this.refreshStepViewModel();
        }
      });
  }

  setupQuiz() {
    if (!this.quiz) {
      return;
    }

    if (this.quiz.TimeLimitMinutes) {
      this.timeLabel = 'Time Remaining: ';
    }

    this.ensureQuizResult();
    this.setupQuestions();

  }

  afterQuizSetupComplete() {

    if (!this.reviewAnswers) {
      if (!this.quizSetup?.IsTestMode) {
        this.setupQuizTimer();
      }
    } else {
      this.timeTakingString = this.dateTimeUtilService.formatDurationMinutesSeconds(this.quizResult.TotalTimeSeconds);
    }
  }

  setupQuizTimer() {
    this.quizStartTime = this.dateTimeUtilService.getCurrentDateTime();
    this.updateQuizTimer();
    this.quizTimerIntervalHandle = setInterval(() => {
      this.updateQuizTimer();

    }, 1000);
  }

  updateQuizTimer() {
    let timeInSeconds = this.dateTimeUtilService.calcDurationSeconds(this.quizStartTime, this.dateTimeUtilService.getCurrentDateTime());

    if (this.quiz.TimeLimitMinutes) {
      const timeLimit = this.quiz.TimeLimitMinutes * 60;

      if (timeInSeconds >= timeLimit) {

        this.submitQuiz();
        return;
      }

      const timeLeft = timeLimit - timeInSeconds;

      // Extended user activity until quiz is completed
      this.inactivityUtilService.extendedInactivitytime();

      timeInSeconds = timeLeft;
    }

    const currentTime = this.dateTimeUtilService.formatDurationMinutesSeconds(timeInSeconds);

    // if (this.quiz.TimeLimitMinutes) {
    //   const timeLimint = this.dateTimeUtilService.formatDurationMinutesSeconds(this.quiz.TimeLimitMinutes * 60);

    //   this.timeString = `${currentTime} of ${timeLimint}`;
    // }

    this.timeString = currentTime;
  }

  ensureQuizResult() {
    if (!this.quizResult) {
      this.quizResult = {
        _itemName: AppMetaDataItemNames.QuizResult,
        QuizId: this.quiz.QuizId,
        StudentCourseId: this.progress?.course?.studentCourse?.StudentCourseId,
        CourseLessonProgressId: this.progress?.lesson?.lessonProgress?.CourseLessonProgressId,
        IsComplete: false,
        CompletedQuestions: 0,
        TotalQuestions: this.quiz.MaxQuestionsToShow || this.quiz.QuizQuestionLink.length,
        QuizStartDateTime: this.dateTimeUtilService.getCurrentDateTime()
      }
    }

    this.quizResult.QuizResultQuestion = this.quizResult.QuizResultQuestion || [];
  }

  message: DwMessage;
  setupQuestions() {

    if (this.providedQuizSetup) {
      this.applyQuizSetup(this.providedQuizSetup);
      return;
    }

    this.quizRepositoryService.getQuizSetup({ QuizId: this.quiz.QuizId, StudentCourseId: this.progress?.course.studentCourse.StudentCourseId, QuizResultId: this.quizResult?.QuizResultId, TestModeOptions: this.testModeOptions })
      .subscribe((quizSetup) => {
        this.applyQuizSetup(quizSetup);
      });
  }

  private applyQuizSetup(quizSetup: QuizSetupResult): void {
        this.quizSetup = quizSetup;
        this.testMode = quizSetup.IsTestMode;

        if (!quizSetup.IsSuccess) {
          this.message = {
            messageBody: quizSetup.ErrorMessage,
            severity: DwMessageSeverity.error
          };
          this.isLoading = false;

          return;
        }

        this.currentQuestionIndex = 0;
        this.totalQuestionCount = this.quizSetup.CurrentQuizQuestionLinkIds.length;

        if (this.totalQuestionCount > 0) {
          this.setupStartingQuestion();

          this.setupCurrentQuestion();
          this.showQuiz = true;

          this.afterQuizSetupComplete();
        } else {
          this.message = {
            messageBody: 'No questions found for this quiz',
            severity: DwMessageSeverity.error
          };
          this.showQuiz = false
        }

        this.isLoading = false;
  }

  setupStartingQuestion() {

    if (!this.startingQuizResultQuestionId) {
      return;
    }

    let questionResult = this.getQuizResultQuestions().find(q => q.QuizResultQuestionId == this.startingQuizResultQuestionId);
    if (!questionResult) {
      return;
    }

    let questionLink = this.quizSetup.AllQuizQuestionLinks.find(ql => ql.QuestionId == questionResult.QuestionId);

    if (!questionLink) {
      return;
    }

    const questionIndex = this.quizSetup.CurrentQuizQuestionLinkIds.indexOf(questionLink.QuizQuestionLinkId);

    if (questionIndex > 0) {
      this.currentQuestionIndex = questionIndex;
    }
  }

  previousQuestion() {
    this.currentQuestionIndex--;

    this.setupCurrentQuestion();
  }

  nextQuestion() {
    this.currentQuestionIndex++;

    this.setupCurrentQuestion();
  }

  grading: boolean = false;
  submitQuiz() {
    if (!this.quizTimerIntervalHandle) {
      return;
    }

    clearInterval(this.quizTimerIntervalHandle);

    if (this.testMode) {
      const result: QuizCompleteResult = {
        quizResult: this.quizResult,
        isCancelled: true
      };

      this.quizComplete.emit(result);
      return;
    }

    this.updateQuestionResultStats();

    // Prevent saving quizzes with no questions.
    if (this.quizResult.QuizResultQuestion.length === 0) {
      return;
    }

    // Notify containers before mutating the shared QuizResult. Daily quiz pages
    // use this boundary to replace the quiz with the grading state immediately.
    this.grading = true;
    this.quizSubmissionStarted.emit();

    this.quizResult.IsComplete = true;
    this.quizResult.QuizResultStatusId = QuizResultStatusAllItems.CompleteUngraded;

    this.quizResult.QuizStopDateTime = this.currentQuestionResult?.QuestionEndDateTime || this.dateTimeUtilService.getCurrentDateTime();

    let totalSessionQuizTime = this.dateTimeUtilService.calcDurationSeconds(this.quizStartTime, this.quizResult.QuizStopDateTime);
    if (this.quiz.TimeLimitMinutes && totalSessionQuizTime > this.quiz.TimeLimitMinutes * 60) {
      totalSessionQuizTime = this.quiz.TimeLimitMinutes * 60;
    }

    this.quizResult.TotalTimeSeconds = (this.quizResult.TotalTimeSeconds || 0) + totalSessionQuizTime;

    this.quizRepositoryService.saveQuizResult(this.quizResult)
      .subscribe({
        next: quizResult => {
          // Don't reset grading before the container replaces this view.
          this.quizResult = quizResult;

          const result: QuizCompleteResult = {
            quizResult: this.quizResult,
            isCancelled: false
          };

          this.quizComplete.emit(result);
        },
        error: () => {
          this.grading = false;
          this.quizSubmissionFailed.emit();
        }
      });
  }

  cancel() {
    const result: QuizCompleteResult = {
      isCancelled: true,
      quizResult: this.quizResult
    };

    this.quizComplete.emit(result);
  }

  questionResultChanged(questionResult: QuizResultQuestionEntity): void {
    const quizResultQuestions = this.getQuizResultQuestions();
    const questionIndex = quizResultQuestions.findIndex(result =>
      questionResult.QuizResultQuestionId
        ? result.QuizResultQuestionId === questionResult.QuizResultQuestionId
        : result.QuestionId === questionResult.QuestionId);

    if (questionIndex >= 0) {
      quizResultQuestions[questionIndex] = questionResult;
    } else {
      quizResultQuestions.push(questionResult);
    }

    this.currentQuestionResult = questionResult;
    this.refreshStepViewModel();
  }

  private setupCurrentQuestion() {
    if (!this.quizSetup || this.currentQuestionIndex == null) {
      return;
    }

    // Update stats on previous question
    this.updateQuestionResultStats();

    // Extend inactivity time when the question changes
    this.inactivityUtilService.extendedInactivitytime();

    this.hasNextQuestion = this.currentQuestionIndex < (this.totalQuestionCount - 1);
    this.hasPreviousQuestion = this.currentQuestionIndex > 0;

    const questionLinkId = this.quizSetup.CurrentQuizQuestionLinkIds[this.currentQuestionIndex];

    if (!questionLinkId) {
      console.warn('No question link id found for index', this.currentQuestionIndex);

      return;
    }

    this.currentQuestionStartTime = this.dateTimeUtilService.getCurrentDateTime();

    this.currentQuestionLink = this.quizSetup.AllQuizQuestionLinks.find(q => q.QuizQuestionLinkId == questionLinkId);

    if (!this.currentQuestionLink) {
      return;
    }

    const quizResultQuestions = this.getQuizResultQuestions();

    this.currentQuestionResult = quizResultQuestions.find(q => q.QuestionId === this.currentQuestionLink.QuestionId);

    if (this.reviewAnswers) {
      this.questionResultIconClass = this.currentQuestionResult?.IsCorrect ? 'fa fa-check dw-green' : 'fa fa-times dw-red',
        this.questionResultTextNote = this.currentQuestionResult?.IsCorrect ? 'Correct' : 'Incorrect';
    }

    if (!this.currentQuestionResult) {
      this.currentQuestionResult = {
        _itemName: AppMetaDataItemNames.QuizResultQuestion,
        QuestionId: this.currentQuestionLink.QuestionId,
        AnswerValue: null,
        QuestionStartDateTime: this.dateTimeUtilService.getCurrentDateTime()
      };

      quizResultQuestions.push(this.currentQuestionResult);
    }

    this.refreshStepViewModel();
  }

  private getQuizResultQuestions(): QuizResultQuestionEntity[] {
    if (!this.quizResult && !this.quiz) {
      return [];
    }

    this.ensureQuizResult();
    this.quizResult.QuizResultQuestion = this.quizResult.QuizResultQuestion || [];

    return this.quizResult.QuizResultQuestion;
  }

  private updateQuestionResultStats() {
    // Update stats on previous question
    if (this.currentQuestionResult) {
      this.currentQuestionResult.QuestionEndDateTime = this.dateTimeUtilService.getCurrentDateTime();

      const questionTime = this.dateTimeUtilService.calcDurationSeconds(this.currentQuestionStartTime, this.currentQuestionResult.QuestionEndDateTime);
      this.currentQuestionResult.TotalTimeSeconds = (this.currentQuestionResult.TotalTimeSeconds || 0) + questionTime;
    }
  }

}
